/**
 * Client-side crypto primitives for account-backed sync.
 *
 * Clerk authenticates the user, but it is not an encryption secret. Bunko
 * encrypts account data with a random AES sync key. In the current account
 * sync model that key is stored server-side per Clerk account, so this protects
 * against casual storage exposure and cross-user reads, not backend recovery.
 */

const AES_NAME = 'AES-GCM';
const RSA_NAME = 'RSA-OAEP';
const RSA_HASH = 'SHA-256';
const SYNC_KEY_BITS = 256;
const SYNC_KEY_BYTES = SYNC_KEY_BITS / 8;
const IV_LEN = 12;
const syncKeyMaterial = new WeakMap<CryptoKey, Uint8Array<ArrayBuffer>>();

export interface EncryptedAccountRecord {
  ciphertext: string;
  iv: string;
}

export interface WrappedSyncKey {
  wrappedKey: string;
  iv: string;
  algorithm: 'RSA-OAEP-256+A256GCM';
}

function newBytes(length: number): Uint8Array<ArrayBuffer> {
  return new Uint8Array(new ArrayBuffer(length));
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const out = newBytes(length);
  crypto.getRandomValues(out);
  return out;
}

export function accountBytesToBase64(bytes: Uint8Array | ArrayBuffer): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (let i = 0; i < view.length; i++) s += String.fromCharCode(view[i] ?? 0);
  return btoa(s);
}

export function accountBase64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64);
  const out = newBytes(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

export async function generateSyncKey(): Promise<CryptoKey> {
  const raw = randomBytes(SYNC_KEY_BYTES);
  return importSyncKeyMaterial(raw);
}

export async function importSyncKeyMaterial(raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const key = await crypto.subtle.importKey(
    'raw',
    raw,
    { name: AES_NAME, length: SYNC_KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
  syncKeyMaterial.set(key, raw);
  return key;
}

export function exportSyncKeyMaterial(syncKey: CryptoKey): string {
  const raw = syncKeyMaterial.get(syncKey);
  if (!raw) throw new Error('sync key material is unavailable on this device');
  return accountBytesToBase64(raw);
}

export async function encryptAccountRecord(
  plaintext: unknown,
  syncKey: CryptoKey,
): Promise<EncryptedAccountRecord> {
  const iv = randomBytes(IV_LEN);
  const encoded = new TextEncoder().encode(JSON.stringify(plaintext));
  const ciphertext = await crypto.subtle.encrypt({ name: AES_NAME, iv }, syncKey, encoded);
  return {
    ciphertext: accountBytesToBase64(ciphertext),
    iv: accountBytesToBase64(iv),
  };
}

export async function decryptAccountRecord<T>(
  encrypted: EncryptedAccountRecord,
  syncKey: CryptoKey,
): Promise<T> {
  const iv = accountBase64ToBytes(encrypted.iv);
  const ciphertext = accountBase64ToBytes(encrypted.ciphertext);
  const plaintext = await crypto.subtle.decrypt({ name: AES_NAME, iv }, syncKey, ciphertext);
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}

export async function generateDeviceKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    {
      name: RSA_NAME,
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: RSA_HASH,
    },
    true,
    ['encrypt', 'decrypt'],
  );
}

export async function exportDevicePublicKey(publicKey: CryptoKey): Promise<JsonWebKey> {
  return crypto.subtle.exportKey('jwk', publicKey);
}

export async function wrapSyncKeyForDevice(
  syncKey: CryptoKey,
  devicePublicKeyJwk: JsonWebKey,
): Promise<WrappedSyncKey> {
  const raw = syncKeyMaterial.get(syncKey);
  if (!raw) {
    throw new Error('sync key material is unavailable on this device');
  }
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    devicePublicKeyJwk,
    { name: RSA_NAME, hash: RSA_HASH },
    false,
    ['encrypt'],
  );
  const wrapped = await crypto.subtle.encrypt({ name: RSA_NAME }, publicKey, raw);
  return {
    wrappedKey: accountBytesToBase64(wrapped),
    iv: '',
    algorithm: 'RSA-OAEP-256+A256GCM',
  };
}

export async function unwrapSyncKeyForDevice(
  wrapped: WrappedSyncKey,
  devicePrivateKey: CryptoKey,
): Promise<CryptoKey> {
  const raw = await crypto.subtle.decrypt(
    { name: RSA_NAME },
    devicePrivateKey,
    accountBase64ToBytes(wrapped.wrappedKey),
  );
  const key = await crypto.subtle.importKey(
    'raw',
    raw,
    { name: AES_NAME, length: SYNC_KEY_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
  syncKeyMaterial.set(key, new Uint8Array(raw));
  return key;
}
